import { useEffect, useRef } from 'react'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useGetLibraryItems from 'Pods/ThreeDStudio/Components/Sidebar/Components/LibraryContent/Hooks/useGetLibraryItems'
import { threeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setIsSidebarOpen } from 'Pods/ThreeDStudio/Store/actions/ui'

import { getBreakPoint } from 'Utils/useBreakpoint'

// On mobile (< 640px) the open library sidebar covers the workspace, so users
// with no generated models land on the library empty state instead of the
// Generate panel. Collapse the sidebar once on initial load for those users so
// they start on Generate. Users with models keep the open-library default, and
// desktop is untouched. Reuses the shared `libraryItems` query (deduped with
// LibraryContent), so no extra fetch is made.
function useInitialMobilePanel({ initialRunSlug = null, extractingCoreStyles = false } = {}) {
  const location = useLocation()
  const pathname = location?.pathname ?? ''
  const isGalleryRoute = pathname.startsWith('/ai/3d-models')

  const pageConfig = usePageConfig()
  const pageConfigLoaded = pageConfig !== undefined
  const isLoggedIn = pageConfig?.isCurrentUser ?? false

  const { items, isLoading, isError, meta } = useGetLibraryItems(isLoggedIn, '')
  const totalCount = meta?.totalCount

  const hasRunRef = useRef(false)

  useEffect(() => {
    if (hasRunRef.current || extractingCoreStyles || isGalleryRoute || !pageConfigLoaded) {
      return
    }

    // A deep-linked model means the user has content to view; keep the default.
    const hasOpenedModel = threeDStudioStore.getState().ui?.openedModel?.id != null
    if (initialRunSlug != null || hasOpenedModel) {
      hasRunRef.current = true
      return
    }

    // Logged-in users: wait for the library to resolve; on error keep the default.
    if (isLoggedIn) {
      if (isError) {
        hasRunRef.current = true
        return
      }
      if (isLoading) {
        return
      }
    }

    hasRunRef.current = true

    const hasModels = isLoggedIn && ((totalCount ?? items.length) > 0)
    if (!hasModels && getBreakPoint(window.innerWidth) === 'xs') {
      setIsSidebarOpen(false)
    }
  }, [
    extractingCoreStyles,
    isGalleryRoute,
    pageConfigLoaded,
    isLoggedIn,
    isLoading,
    isError,
    items.length,
    totalCount,
    initialRunSlug,
  ])
}

export default useInitialMobilePanel
